// src/components/tarbaa/Question.jsx
import React, { useState, useRef, useEffect } from "react";
import { FaPause, FaPlay, FaListUl } from "react-icons/fa";
import {
  FaClockRotateLeft,
  FaMoneyBillTransfer,
  FaXmark,
  FaForward,
  FaBackward,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa6";
import { HiPhone } from "react-icons/hi2";

import ReactPlayer from "react-player";
import { useStopwatch, useTimer } from "react-timer-hook";
import textSizeBasedOnLength from "../../funcs/textResizer";
import { encryptData } from "../../funcs/encryption";
import { truncateText } from "../../utils/textUtils";
import {
  getAnswerStats,
  getFirstMeaningfulLetter,
} from "../../utils/arabicTextUtils";
import TextModal from "../modals/TextModal";
import NoWrongAnswersModal from "../modals/NoWrongAnswersModal";
import * as webGmBridge from "../../live/webGmBridge";
import DrawBoard from "../../draw/DrawBoard";
import { useGmTopVote } from "../../live/useGmLive";

const Question = ({ game, ads, question, setShowAnswer }) => {
  // Live broadcast: spectators' top-voted action (glow on the master's own
  // controls) — null when not broadcasting.
  const liveTopVote = useGmTopVote("question");
  const [fullScreen, setFullScreen] = useState(false);
  const [selectedPowerUp, setSelectedPowerUp] = useState({ name: null });
  const [showMultipleChoice, setShowMultipleChoice] = useState(false);
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] = useState([]);
  const [isChoicesMinimized, setIsChoicesMinimized] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showNoWrongAnswersModal, setShowNoWrongAnswersModal] = useState(false);
  const [alternativePowerUpUsed, setAlternativePowerUpUsed] = useState(false);
  const [selectedTeamForModal, setSelectedTeamForModal] = useState(null);
  const [showFirstLetterHint, setShowFirstLetterHint] = useState(false);
  const [firstLetterHint, setFirstLetterHint] = useState("");
  const [isFirstLetterMinimized, setIsFirstLetterMinimized] = useState(false);
  const [showAnswerPattern, setShowAnswerPattern] = useState(false);
  const [answerPatternInfo, setAnswerPatternInfo] = useState(null);
  const [isAnswerPatternMinimized, setIsAnswerPatternMinimized] =
    useState(false);
  const { seconds, minutes, isRunning, start, pause, reset } = useStopwatch({
    autoStart: true,
  });
  const callTimer = useTimer({
    expiryTimestamp: Date.now() + 30000,
    autoStart: false,
  });

  // Audio player state
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPosition, setAudioPosition] = useState(0);
  // eslint-disable-next-line
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [answers, setAnswers] = useState([]);

  const modeName = game?.modes?.find((mode) =>
    mode.questions.find(
      (q) => (q?.question?._id || q?.customQuestion?._id) === question?._id
    )
  )?.name;

  const hasWrongAnswers = !!(
    question?.wrongAnswers && question?.wrongAnswers.length > 0
  );

  // Live broadcast: mirror active power-ups (choices grid / first letter /
  // answer pattern / phone call) + per-team used flags to spectators, so the
  // viewer sees exactly what this screen shows. No-op when not broadcasting.
  useEffect(() => {
    if (!webGmBridge.isActive()) return;
    webGmBridge.emitState({
      powerUps: {
        ...webGmBridge.powerUpsFromGame(game),
        active: {
          multipleAnswers:
            showMultipleChoice && multipleChoiceAnswers.length
              ? { choices: multipleChoiceAnswers }
              : null,
          firstLetter:
            showFirstLetterHint && firstLetterHint
              ? { letter: firstLetterHint }
              : null,
          pattern: showAnswerPattern && answerPatternInfo ? answerPatternInfo : null,
          phone: callTimer.isRunning ? {} : null,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showMultipleChoice,
    multipleChoiceAnswers,
    showFirstLetterHint,
    firstLetterHint,
    showAnswerPattern,
    answerPatternInfo,
    callTimer.isRunning,
    selectedPowerUp,
  ]);

  const handleFinishPowerUp = () => {
    const team = game.teams.find((t) => t._id === selectedPowerUp.team._id);
    const powerUp = team.powerUps.find((p) => p.name === selectedPowerUp.name);
    if (powerUp) {
      powerUp.used = true;
      // Encrypt and save updated game data
      localStorage.setItem("game", encryptData(game));
      setSelectedPowerUp({ name: null });
    }
  };

  const handleUseShowFirstLetter = () => {
    if (selectedTeamForModal) {
      // Create and show first letter hint - just the first letter
      const answer = question?.answer || question?.customQuestion?.answer || "";
      const { firstLetter } = getFirstMeaningfulLetter(answer);
      setFirstLetterHint(firstLetter);
      setShowFirstLetterHint(true);

      // Mark multiple choice powerup as used (not the other powerups)
      const team = game.teams.find((t) => t._id === selectedTeamForModal._id);
      const powerUp = team.powerUps.find(
        (p) => p.name !== "one" && p.name !== "two"
      );
      if (powerUp) {
        powerUp.used = true;
        localStorage.setItem("game", encryptData(game));
      }

      setAlternativePowerUpUsed(true);
      setShowNoWrongAnswersModal(false);
      setSelectedTeamForModal(null);
    }
  };

  const handleUseShowAnswerPattern = () => {
    if (selectedTeamForModal) {
      // Create and show answer pattern
      const answer = question?.answer || question?.customQuestion?.answer || "";
      const stats = getAnswerStats(answer);
      setAnswerPatternInfo(stats);
      setShowAnswerPattern(true);

      // Mark multiple choice powerup as used (not the other powerups)
      const team = game.teams.find((t) => t._id === selectedTeamForModal._id);
      const powerUp = team.powerUps.find(
        (p) => p.name !== "one" && p.name !== "two"
      );
      if (powerUp) {
        powerUp.used = true;
        localStorage.setItem("game", encryptData(game));
      }

      setAlternativePowerUpUsed(true);
      setShowNoWrongAnswersModal(false);
      setSelectedTeamForModal(null);
    }
  };

  // Audio player functions
  const handleAudioPlayPause = () => {
    // Simply toggle the playing state, which controls ReactPlayer via the playing prop
    setIsAudioPlaying(!isAudioPlaying);
  };

  const handleAudioSkipForward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(currentTime + 15);
    }
  };

  const handleAudioSkipBackward = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.getCurrentTime();
      audioRef.current.seekTo(Math.max(0, currentTime - 15));
    }
  };

  const handleAudioProgress = (state) => {
    setAudioPosition(state.playedSeconds);
  };

  const handleAudioDuration = (duration) => {
    setAudioDuration(duration);
    setAudioLoaded(true);
  };

  const handleAudioSeek = (e) => {
    const seekValue = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.seekTo(seekValue);
    }
    setAudioPosition(seekValue);
  };

  // Format time in mm:ss
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    if (hasWrongAnswers) {
      setAnswers(
        [...question?.wrongAnswers, question?.answer].sort(
          () => Math.random() - 0.5
        )
      );
    }
  }, [question, hasWrongAnswers]);

  useEffect(() => {
    if (showMultipleChoice && question?.wrongAnswers) {
      setMultipleChoiceAnswers(
        [...question.wrongAnswers, question.answer].sort(
          () => Math.random() - 0.5
        )
      );
    }
  }, [showMultipleChoice, question]);
  const questionText = question?.question || "";
  const { truncated: truncatedQuestion, needsTruncation } = truncateText(
    questionText,
    120
  );

  // Render media content based on what's available
  const renderMediaContent = () => {
    if (question?.questionVideo && question?.questionVideo.length > 10) {
      return (
        <ReactPlayer
          url={question?.questionVideo}
          controls
          width="100%"
          height="100%"
        />
      );
    } else if (question?.questionAudio && question?.questionAudio.length > 10) {
      return (
        <div className="w-full h-full flex flex-col justify-center items-center">
          <div className="w-full max-w-md bg-gray-100 p-6 rounded-xl shadow-md">
            {/* Audio Player - Custom UI similar to mobile app */}
            <div className="flex flex-col items-center gap-5">
              {/* Play Controls */}
              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={handleAudioSkipBackward}
                  className="text-dark hover:text-gray-700"
                >
                  <FaBackward size={24} />
                </button>

                <button
                  onClick={handleAudioPlayPause}
                  className="text-dark hover:text-gray-700 bg-gray-200 rounded-full p-3"
                >
                  {isAudioPlaying ? (
                    <FaPause size={32} />
                  ) : (
                    <FaPlay size={32} />
                  )}
                </button>

                <button
                  onClick={handleAudioSkipForward}
                  className="text-dark hover:text-gray-700"
                >
                  <FaForward size={24} />
                </button>
              </div>

              {/* Time display */}
              <div className="text-dark text-center mt-2">
                {formatTime(audioPosition)} / {formatTime(audioDuration)}
              </div>

              {/* Progress slider */}
              <div className="w-full mt-2">
                <input
                  type="range"
                  min={0}
                  max={audioDuration || 100}
                  value={audioPosition || 0}
                  onChange={handleAudioSeek}
                  className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>

            {/* Hidden actual player for controlling audio */}
            <div className="hidden">
              <ReactPlayer
                ref={audioRef}
                url={question?.questionAudio}
                width="0"
                height="0"
                playing={isAudioPlaying}
                onProgress={handleAudioProgress}
                onDuration={handleAudioDuration}
                onEnded={() => setIsAudioPlaying(false)}
                config={{
                  file: {
                    forceAudio: true,
                  },
                }}
              />
            </div>
          </div>
        </div>
      );
    } else if (question?.drawQrCodeId) {
      return (
        <DrawBoard
          qrCodeId={question.drawQrCodeId}
          fallbackImage={question?.questionImage}
          alt={question?.question}
        />
      );
    } else if (question?.questionImage) {
      return (
        <button
          onClick={() => {
            setFullScreen(true);
          }}
          className="h-full"
        >
          <img
            className="h-full object-contain"
            src={question?.questionImage}
            alt={question?.question}
          />
        </button>
      );
    } else if (hasWrongAnswers) {
      // Display wrong answers for custom questions
      return (
        <div className="w-full h-full flex flex-col justify-center items-center">
          <div className="text-2xl font-bold mb-6" style={{ direction: "rtl" }}>
            الخيارات:
          </div>
          <div
            className="flex flex-row flex-wrap w-full max-w-lg justify-center items-center"
            style={{ direction: "rtl" }}
          >
            {/* Correct answer mixed with wrong answers */}
            {answers.map((option, index) => (
              <div key={index} className=" px-2 py-2 w-1/2">
                <div className="bg-gray-100 p-4 rounded-xl shadow-md text-center text-lg">
                  {option}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      // No media - display a placeholder or message
      return (
        <div className="w-full h-full flex justify-center items-center"></div>
      );
    }
  };

  return (
    <>
      <div className="w-full h-[90vh] flex flex-col portrait:flex-col sm:flex-row portrait:py-10 pl-10 pr-2 py-10 xl:py-20">
        <div
          className={`portrait:w-full portrait:h-3/4 relative h-3/4 w-full sm:w-5/6 sm:h-full ${textSizeBasedOnLength(
            question?.question
          )} font-bold text-dark border-8 border-behindButton rounded-[50px] pt-4 sm:pt-6 md:pt-8`}
        >
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 sm:w-full flex flex-row justify-between items-center bg-transparent">
            {/* Left Section */}
            <div className="w-1/3 flex justify-center items-center">
              <div
                className={`hidden sm:block border-[#343434] border-4 rounded-2xl ${
                  question?.spare ? "bg-behindButton" : ""
                } ${question?.custom ? "bg-mainText" : ""}`}
              >
                {question?.custom && (
                  <div className="text-center px-2 text-dark rounded-lg text-sm lg:text-xl">
                    فئة خاصة
                  </div>
                )}
                {question?.spare && (
                  <div className="text-center px-2 text-white rounded-lg text-sm lg:text-xl">
                    وسيلة مساعدة
                  </div>
                )}
                <div className="text-center p-1 px-4 lg:p-2 bg-[#343434] text-white rounded-lg text-sm lg:text-xl ">
                  {question?.spare
                    ? game?.teams?.find((team) => team.currentTurn).name
                    : modeName}
                </div>
              </div>
            </div>

            {/* Center Section (Timer Controls) */}
            <div className="w-1/3 flex justify-center items-center">
              <div className="p-1 px-4 md:p-4 bg-[#343434] text-white rounded-full flex gap-10 text-lg lg:text-3xl">
                <button onClick={() => (isRunning ? pause() : start())}>
                  {isRunning ? <FaPause /> : <FaPlay />}
                </button>
                <h1 className="text-center">
                  {minutes < 10 && "0"}
                  {minutes}:{seconds < 10 && "0"}
                  {seconds}
                </h1>
                <button onClick={() => reset()}>
                  <FaClockRotateLeft />
                </button>
              </div>
            </div>

            {/* Right Section */}
            <div className="w-1/3 flex justify-center items-center">
              <div className="hidden sm:block border-[#343434] border-4 rounded-2xl">
                <div className="text-right p-1 px-4 lg:p-2 bg-[#343434] text-white rounded-lg text-sm lg:text-xl flex gap-1">
                  <div>نقطة</div>
                  <div>{question?.points}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="h-2/6 xl:h-1/6 w-full flex flex-col justify-center items-center p-2 px-4 lg:px-10">
            <div className="flex-1 flex items-center justify-center">
              <h1
                style={{ direction: "rtl", textAlign: "center" }}
                className="leading-relaxed"
              >
                {truncatedQuestion}
              </h1>
            </div>
            {needsTruncation && (
              <button
                onClick={() => setShowTextModal(true)}
                className="mt-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
              >
                عرض أكثر
              </button>
            )}
          </div>
          <div className="h-4/6 xl:h-5/6 w-full flex justify-center items-start px-4 pb-4 xl:p-10">
            {renderMediaContent()}
          </div>

          {showMultipleChoice && question?.wrongAnswers && (
            <div
              className={`absolute ${
                isChoicesMinimized
                  ? "bottom-4 right-4"
                  : "bottom-10 left-1/2 transform -translate-x-1/2"
              } transition-all duration-300`}
            >
              <div
                className={`bg-white rounded-xl shadow-2xl border-4 border-behindButton ${
                  isChoicesMinimized ? "w-auto" : "w-[90vw] max-w-lg"
                }`}
              >
                <div className="bg-behindButton p-3 rounded-t-lg flex justify-between items-center">
                  <button
                    onClick={() => setIsChoicesMinimized(!isChoicesMinimized)}
                    className="text-white hover:bg-[#00000020] rounded-full p-1 transition-colors"
                  >
                    {isChoicesMinimized ? (
                      <FaChevronUp size={20} />
                    ) : (
                      <FaChevronDown size={20} />
                    )}
                  </button>
                  <h3
                    className="text-white font-bold text-lg flex-1 text-center"
                    style={{ direction: "rtl" }}
                  >
                    خيارات متعددة
                  </h3>
                  <div className="w-8" />
                </div>

                {!isChoicesMinimized && (
                  <div className="p-4">
                    <p
                      className="text-center text-gray-600 text-sm mb-4"
                      style={{ direction: "rtl" }}
                    >
                      الإجابة الصحيحة موجودة بين هذه الخيارات
                    </p>
                    <div
                      className="grid grid-cols-2 gap-3"
                      style={{ direction: "rtl" }}
                    >
                      {multipleChoiceAnswers.map((option, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg text-center font-semibold border-2 border-gray-200 relative overflow-hidden"
                        >
                          <div className="absolute top-1 left-1 w-8 h-8 bg-behindButton rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {String.fromCharCode(65 + index)}
                          </div>
                          <div className="mt-2">{option}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="absolute bottom-0   transform translate-x-1/2 translate-y-1/2 ">
            <button
              className={`relative bg-mainButton hover:bg-mainButtonShadow text-white font-bold py-3 px-4 md:px-10 border-b-8 border-mainButtonShadow hover:border-mainButtonShadow rounded-md text-sm md:text-xl ${
                liveTopVote?.optionKey === "reveal" ? "ring-4 ring-mainText" : ""
              }`}
              onClick={() => {
                setShowAnswer(true);
              }}
            >
              {liveTopVote?.optionKey === "reveal" && (
                <span className="absolute -top-2 -left-2 min-w-5 h-5 px-1 rounded-full bg-mainText text-header text-xs font-bold flex items-center justify-center z-20 shadow">
                  {liveTopVote.count}
                </span>
              )}
              الإجابة
            </button>
          </div>
        </div>
        <div className=" portrait:h-1/4 portrait:w-full  h-1/4 w-full  sm:w-1/4 sm:h-full flex-row sm:flex-col portrait:flex-row flex justify-center items-center text-white text-4xl sm:pl-4 sm:gap-2 portrait:pt-6 pt-6 sm:pt-0">
          {game?.teams?.map((team, index) => (
            <div
              className="portrait:w-1/4 portrait:h-full w-full h-1/4 px-[1px] xl:p-2"
              key={index}
            >
              <div
                className={`portrait:flex-col  w-full h-full flex flex-col sm:flex-row justify-center items-center  bg-[#dd9c6e] rounded-lg        ${
                  team.currentTurn === true && "border-4 border-dark"
                }`}
              >
                <div className="portrait:w-full portrait:h-1/2 w-full h-1/2 sm:w-1/3  sm:h-full flex  justify-center items-center p-1 sm:p-2">
                  <div className=" h-full  aspect-square rounded-full overflow-hidden flex justify-center items-center">
                    <div className=" w-full aspect-square rounded-full overflow-hidden ">
                      <img
                        src={team?.image?.image}
                        alt={team?.name}
                        className="w-full h-full object-cover "
                      />
                    </div>
                  </div>
                </div>
                <div className="h-1/2 w-full sm:w-2/3 sm:h-full text-sm md:text-xl xl:text-2xl font-bold ">
                  <div className="h-1/2 flex items-center justify-center overflow-hidden">
                    <h1 className="text-white text-center">{team?.name}</h1>
                  </div>
                  <div className="portrait:w-full portrait:h-1/2 w-full h-1/2 flex justify-center items-center p-2 gap-1 md:gap-4">
                    {team?.powerUps.map((powerUp, index) => {
                      const isLiveTopVoted =
                        team.currentTurn &&
                        !powerUp.used &&
                        liveTopVote?.optionKey === `powerup:${powerUp?.name}`;
                      return (
                        <button
                          disabled={
                            powerUp.used ||
                            (!team.currentTurn && powerUp?.name === "one") ||
                            (powerUp?.name !== "one" &&
                              powerUp?.name !== "two" &&
                              !hasWrongAnswers &&
                              alternativePowerUpUsed)
                          }
                          onClick={() => {
                            if (!powerUp.used) {
                              // Check if it's multiple choice powerup and no wrong answers
                              if (
                                powerUp?.name !== "one" &&
                                powerUp?.name !== "two" &&
                                !hasWrongAnswers
                              ) {
                                setSelectedTeamForModal(team);
                                setShowNoWrongAnswersModal(true);
                              } else {
                                setSelectedPowerUp({ name: powerUp?.name, team });
                              }
                            }
                          }}
                          className={`relative bg-mainButtonShadow hover:bg-mainButton rounded-md md:rounded-lg p-1 md:p-2 text-sm  md:text-lg xl:text-2xl disabled:bg-[#94674690] disabled:text-[#ffffff90] ${
                            isLiveTopVoted ? "ring-4 ring-mainText" : ""
                          }`}
                          key={index}
                        >
                          {isLiveTopVoted && (
                            <span className="absolute -top-2 -left-2 min-w-5 h-5 px-1 rounded-full bg-mainText text-header text-xs font-bold flex items-center justify-center z-20 shadow">
                              {liveTopVote.count}
                            </span>
                          )}
                          {powerUp?.name === "one" ? (
                            <FaMoneyBillTransfer />
                          ) : powerUp?.name === "two" ? (
                            <HiPhone />
                          ) : (
                            <FaListUl />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {fullScreen && (
        <button
          onClick={() => {
            setFullScreen(false);
          }}
          className="absolute h-[100vh] w-[100vw] bg-[#00000090]"
        >
          <img
            src={question?.questionImage}
            alt={question?.question}
            className="h-full w-full object-contain"
          />
        </button>
      )}
      {selectedPowerUp.name && (
        <>
          <div className="z-[9999999] justify-center items-center flex fixed inset-0 outline-none focus:outline-none text-right">
            <div className="relative w-auto my-6 mx-auto max-w-lg md:max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className=" flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t gap-6">
                  <div>
                    <button
                      className=" p-1 ml-auto bg-transparent border-0 text-black opacity-15 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                      onClick={() => setSelectedPowerUp({ name: null })}
                    >
                      <FaXmark />
                    </button>
                  </div>
                  <div className="flex flex-row justify-end items-center gap-2">
                    <h1>{selectedPowerUp.team.name}</h1>
                    <img
                      className="h-10 aspect-square rounded-full"
                      src={selectedPowerUp?.team?.image?.image}
                      alt={selectedPowerUp?.team?.name}
                    />
                  </div>
                  <div className=" flex flex-col gap-2">
                    <h3 className="text-3xl font-semibold text-right">
                      {selectedPowerUp.name === "one"
                        ? "ما عجبني بغيّره"
                        : selectedPowerUp.name === "two"
                        ? "اتصال بصديق"
                        : "خيارات متعددة"}
                    </h3>
                  </div>
                </div>
                {/*body*/}
                <div className="relative p-6 flex flex-col justify-center items-center gap-5">
                  <h1 className="text-center font-bold">
                    {selectedPowerUp.name === "one"
                      ? "تقدر تغيّر السؤال حق سؤال عشوائي ثاني بس ها! اذا جاوبته صح بتاخذ نقاط السؤال اليديد"
                      : selectedPowerUp.name === "two"
                      ? "أول ما يرد الي داقين عليه تبلشون التايمر"
                      : "لما تستعملها بتطلع لك خيارات متعددة فيها الإجابة الصحيحة"}
                  </h1>
                  {selectedPowerUp.name === "two" && (
                    <>
                      <button
                        className="border-4 h-36 aspect-square rounded-full overflow-hidden  font-semibold text-2xl"
                        onClick={() => callTimer.resume()}
                      >
                        <h1
                          className={`${
                            callTimer.totalSeconds > 15 &&
                            callTimer.totalSeconds <= 30 &&
                            callTimer.isRunning
                              ? "text-green-400"
                              : callTimer.totalSeconds > 0 &&
                                callTimer.totalSeconds <= 15
                              ? "text-yellow-400"
                              : callTimer.totalSeconds === 0
                              ? "text-red-400"
                              : "text-dark"
                          }`}
                        >
                          {callTimer.totalSeconds > 0 &&
                          callTimer.totalSeconds <= 30 &&
                          callTimer.isRunning ? (
                            (callTimer < 10 && "0") + callTimer.totalSeconds
                          ) : callTimer.totalSeconds === 0 ? (
                            "انتهى الوقت"
                          ) : (
                            <div className=" flex justify-center items-center flex-col">
                              <h1>بلش التايمر</h1>
                              <HiPhone />
                            </div>
                          )}
                        </h1>
                      </button>
                    </>
                  )}
                  <button
                    onClick={
                      selectedPowerUp.name === "one"
                        ? () => {
                            const randomQuestion =
                              game.spareQuestions[
                                Math.floor(
                                  Math.random() * game.spareQuestions.length
                                )
                              ];
                            game.spareQuestions = game.spareQuestions.filter(
                              (q) => q._id !== randomQuestion._id
                            );
                            // Replace the current question in the mode with the new question
                            const mode = game.modes.find((mode) =>
                              mode.questions.find(
                                (q) =>
                                  (q?.question?._id ||
                                    q?.customQuestion?._id) === question?._id
                              )
                            );
                            const qIndex = mode.questions.findIndex(
                              (q) =>
                                (q?.question?._id || q?.customQuestion?._id) ===
                                question?._id
                            );
                            if (qIndex !== -1) {
                              mode.questions[qIndex].question = {
                                ...randomQuestion,
                                spare: true,
                              };
                            }
                            // Encrypt and save
                            localStorage.setItem("game", encryptData(game));
                            setSelectedPowerUp({ name: null });
                            handleFinishPowerUp();
                            if (!hasWrongAnswers)
                              setAlternativePowerUpUsed(true);
                            window.location.reload();
                          }
                        : selectedPowerUp.name === "two"
                        ? () => {
                            callTimer.restart(new Date(Date.now() + 30000));
                            callTimer.pause();
                            handleFinishPowerUp();
                            if (!hasWrongAnswers)
                              setAlternativePowerUpUsed(true);
                          }
                        : () => {
                            setShowMultipleChoice(true);
                            handleFinishPowerUp();
                          }
                    }
                    className="bg-secondaryButton text-white p-4 rounded-xl"
                  >
                    {selectedPowerUp.name === "one"
                      ? "غيّر السؤال"
                      : selectedPowerUp.name === "two"
                      ? "خلصت المكالمة"
                      : "إستعمال"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
      {/* Text Modal */}
      <TextModal
        isOpen={showTextModal}
        onClose={() => setShowTextModal(false)}
        text={questionText}
        title="نص السؤال"
      />

      {/* No Wrong Answers Modal */}
      <NoWrongAnswersModal
        isOpen={showNoWrongAnswersModal}
        onClose={() => {
          setShowNoWrongAnswersModal(false);
          setSelectedTeamForModal(null);
        }}
        onUseShowFirstLetter={handleUseShowFirstLetter}
        onUseShowAnswerPattern={handleUseShowAnswerPattern}
        teamName={selectedTeamForModal?.name}
        themeColors={{
          primary: "#946746",
          primaryHover: "#7a5438",
          background: "#f7f3f0",
          border: "#ddbfa6",
        }}
      />

      {/* First Letter Hint - Absolute positioned */}
      {showFirstLetterHint && firstLetterHint && (
        <div className="fixed bottom-4 right-4 z-40">
          <div
            className={`bg-white rounded-xl shadow-2xl border-3 border-mainButton transition-all duration-300 ${
              isFirstLetterMinimized ? "w-auto" : "w-48"
            }`}
          >
            <div className="bg-mainButton p-2 rounded-t-lg flex justify-between items-center">
              <button
                onClick={() =>
                  setIsFirstLetterMinimized(!isFirstLetterMinimized)
                }
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                {isFirstLetterMinimized ? (
                  <FaChevronUp size={16} />
                ) : (
                  <FaChevronDown size={16} />
                )}
              </button>
              <h3
                className="text-white font-bold text-sm flex-1 text-center"
                style={{ direction: "rtl" }}
              >
                الحرف الأول
              </h3>
              <button
                onClick={() => setShowFirstLetterHint(false)}
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                <FaXmark size={16} />
              </button>
            </div>

            {!isFirstLetterMinimized && (
              <div className="p-4">
                <p
                  className="text-center text-mainButton text-xs mb-3"
                  style={{ direction: "rtl" }}
                >
                  الحرف الأول من الإجابة
                </p>
                <p
                  className="text-4xl font-bold text-gray-800 text-center"
                  style={{ direction: "rtl" }}
                >
                  {firstLetterHint}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Answer Pattern Hint - Absolute positioned */}
      {showAnswerPattern && answerPatternInfo && (
        <div
          className="fixed bottom-4 right-4 z-40"
          style={{
            bottom: showFirstLetterHint ? "88px" : "16px",
          }}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl border-3 border-mainButton transition-all duration-300 ${
              isAnswerPatternMinimized ? "w-auto" : "w-64"
            }`}
          >
            <div className="bg-mainButton p-2 rounded-t-lg flex justify-between items-center">
              <button
                onClick={() =>
                  setIsAnswerPatternMinimized(!isAnswerPatternMinimized)
                }
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                {isAnswerPatternMinimized ? (
                  <FaChevronUp size={16} />
                ) : (
                  <FaChevronDown size={16} />
                )}
              </button>
              <h3
                className="text-white font-bold text-sm flex-1 text-center"
                style={{ direction: "rtl" }}
              >
                نمط الإجابة
              </h3>
              <button
                onClick={() => setShowAnswerPattern(false)}
                className="text-white hover:bg-[#ffffff20] rounded-full p-1 transition-colors"
              >
                <FaXmark size={16} />
              </button>
            </div>

            {!isAnswerPatternMinimized && (
              <div className="p-4">
                <p
                  className="text-center text-mainButton text-xs mb-3"
                  style={{ direction: "rtl" }}
                >
                  نمط الكلمات والأحرف
                </p>
                <div className="text-center space-y-3">
                  <p
                    className="text-2xl font-bold text-gray-800"
                    style={{ direction: "rtl", letterSpacing: "0.1em" }}
                  >
                    {answerPatternInfo.pattern}
                  </p>
                  <p className="text-sm text-gray-600">
                    {answerPatternInfo.wordCount} كلمة •{" "}
                    {answerPatternInfo.charCount} حرف
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Question;
